import React, { useEffect, useState } from "react";
import { useOrganisations } from "../Auth/useOrganisations";
import { getMenusreadyToServe, saveInventoryToAPI } from "../Services/MenuApi";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GifLoader from "../Components/loader";
import "react-toastify/dist/ReactToastify.css";
import {
  getCaissesByOrganisation,
  getCaisseStock,
  saveStockCaisses,
} from "../Services/CaisseApi";
import DistributeStockModal from "../Components/StockDistribution";
import CaisseStockModal from "../Components/CaisseStockModal";
import { getImageUrl } from "../Helpers/Helpers";

const Stocks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [organisations, , usedOrganisations] = useOrganisations();
  const [menus, setMenus] = useState([]);
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    limit: 20,
    page: 1,
  });
  const [sortConfig, setSortConfig] = useState({
    key: "titre",
    direction: "ascending",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [stats, setStats] = useState({
    total: 0,
    inStock: 0,
    lowStock: 0,
    outOfStock: 0,
  });

  // Distribution Modal State
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [caisseStocks, setCaisseStocks] = useState({});
  const [showCaisseStockModal, setShowCaisseStockModal] = useState(false);
  const [selectedProductForCaisse, setSelectedProductForCaisse] =
    useState(null);

  useEffect(() => {
    if (usedOrganisations) {
      loadCaisses();
      loadMenus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedOrganisations]);

  useEffect(() => {
    if (usedOrganisations) {
      loadMenus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedOrganisations, filters.page]);

  useEffect(() => {
    if (menus.length > 0 && caisses.length > 0) {
      loadCaisseStocks();
    }
  }, [menus, caisses]);

  const loadMenus = async (overrides = {}) => {
    try {
      setLoading(true);
      const query = { ...filters, ...overrides };
      const response = await getMenusreadyToServe(usedOrganisations, {
        page: query.page,
        limit: query.limit,
        search: query.search,
        status: query.status,
        sortKey: overrides.sortKey ?? sortConfig.key,
        sortDir: overrides.sortDir ?? sortConfig.direction,
      });
      if (response.data.success) {
        const {
          products,
          pagination: paginationInfo,
          statusCounts,
        } = response.data.data;

        const menusWithStatus = products.map((menu) => ({
          ...menu,
          main_stock_quantity: menu.main_stock?.quantite_stock || 0,
          total_stock: menu.total_stock || 0,
          caisse_stocks: menu.caisse_stocks || [],
        }));

        setMenus(menusWithStatus);
        setPagination(paginationInfo);
        setStats({
          total: paginationInfo.total,
          inStock: statusCounts?.normal || 0,
          lowStock: statusCounts?.lowStock || 0,
          outOfStock: statusCounts?.outOfStock || 0,
        });
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  const loadCaisses = async () => {
    try {
      const response = await getCaissesByOrganisation(usedOrganisations);
      if (response.data.success) {
        setCaisses(response.data.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des caisses");
    }
  };

  const loadCaisseStocks = async () => {
    try {
      const stocks = {};
      for (const caisse of caisses) {
        const response = await getCaisseStock(caisse.id);
        if (response.data.success) {
          stocks[caisse.id] = response.data.data;
        }
      }
      setCaisseStocks(stocks);
    } catch (error) {
      console.error("Error loading caisse stocks:", error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "normal":
        return "success";
      case "lowStock":
        return "warning";
      case "outOfStock":
        return "primary";
      default:
        return "secondary";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "normal":
        return t("En Stock");
      case "lowStock":
        return t("Stock Bas");
      case "outOfStock":
        return t("Rupture");
      default:
        return t("Non Géré");
    }
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    setFilters((prev) => ({ ...prev, page: 1 }));
    loadMenus({ page: 1, sortKey: key, sortDir: direction });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const search = searchInput.trim();
    setFilters((prev) => ({ ...prev, search, page: 1 }));
    loadMenus({ search, page: 1 });
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setFilters({ search: "", status: "all", limit: 20, page: 1 });
    loadMenus({ search: "", status: "all", limit: 20, page: 1 });
  };

  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setFilters((prev) => ({ ...prev, page }));
  };

  const getTotalCaisseStock = (productId) => {
    // First try from the menu data (if available)
    const menu = menus.find((m) => m._id === productId);
    if (menu && menu.total_stock !== undefined) {
      const mainStock = menu.main_stock_quantity || 0;
      return menu.total_stock - mainStock;
    }

    // Fallback to calculating from caisseStocks
    let total = 0;
    Object.values(caisseStocks).forEach((caisseStock) => {
      const item = caisseStock.find(
        (s) => s.produit_id === productId || s.product_id === productId,
      );
      if (item) {
        total += item.quantite_stock || 0;
      }
    });
    return total;
  };

  const getMainStockQuantity = (menu) => {
    // Try multiple possible locations for main stock
    return (
      menu.main_stock?.quantite_stock ||
      menu.main_stock_quantity ||
      menu.stock?.quantite_stock ||
      0
    );
  };

  const getCaisseStockForProduct = (productId, caisseId) => {
    console.log(productId, caisses);
    // First try from caisseStocks state
    const stockFromState = caisseStocks[caisseId]?.find(
      (s) => s.produit_id === productId || s.product_id === productId,
    )?.quantite_stock;

    if (stockFromState !== undefined) return stockFromState;

    // Then try from menu.caisse_stocks if available
    const menu = menus.find((m) => m._id === productId);
    if (menu && menu.caisse_stocks) {
      const caisseStock = menu.caisse_stocks.find(
        (cs) =>
          cs.caisse_id === caisseId ||
          cs.caisse_id?.toString() === caisseId?.toString(),
      );
      return caisseStock?.quantite_stock || 0;
    }

    return 0;
  };

  const handleDistributeStock = (product) => {
    setSelectedProduct(product);
    setShowDistributeModal(true);
  };

  const handleViewCaisseStock = (product) => {
    setSelectedProductForCaisse(product);
    setShowCaisseStockModal(true);
  };

  const handleDistributeSubmit = async (distributionData) => {
    try {
      setLoading(true);
      const response = await saveStockCaisses(
        distributionData.productId,
        distributionData.distributions,
        usedOrganisations,
      );
      if (response.data.success) {
        toast.success("Stock distribué avec succès");
        await loadCaisseStocks(); // Reload caisse stocks
        await loadMenus(); // Reload main stock
        setShowDistributeModal(false);
        setSelectedProduct(null);
      } else {
        toast.error(
          response.data.errorMessage || "Erreur lors de la distribution",
        );
      }
    } catch (error) {
      toast.error("Erreur lors de la distribution du stock");
    } finally {
      setLoading(false);
    }
  };

  const sortedMenus = menus;

  return (
    <section className="content">
      <GifLoader loading={loading} />

      <div className="row">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              {/* Filtres */}
              <div className="row mb-4">
                <div className="col-12">
                  <div className="box">
                    <div className="box-body">
                      <div className="row align-items-end">
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>{t("Rechercher")}</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={t(
                                "Rechercher par nom, référence...",
                              )}
                              value={searchInput}
                              onChange={(e) => setSearchInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSearch(e);
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>{t("Filtrer par statut")}</label>
                            <select
                              className="form-control"
                              value={filters.status}
                              onChange={(e) =>
                                handleFilterChange("status", e.target.value)
                              }
                            >
                              <option value="all">{t("Tous les statuts")}</option>
                              <option value="normal">{t("En Stock")}</option>
                              <option value="lowStock">{t("Stock Bas")}</option>
                              <option value="outOfStock">{t("Rupture")}</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>{t("Limite")}</label>
                            <select
                              className="form-control"
                              value={filters.limit}
                              onChange={(e) =>
                                handleFilterChange(
                                  "limit",
                                  parseInt(e.target.value),
                                )
                              }
                            >
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                              <option value="200">200</option>
                            </select>
                          </div>
                        </div>

                        <div className="col-md-2">
                          <div className="form-group">
                            <label>&nbsp;</label>
                            <div className="d-flex" style={{ gap: "8px" }}>
                              <button
                                className="btn btn-danger"
                                onClick={handleSearch}
                                title={t("Rechercher")}
                              >
                                <i className="fa fa-search"></i>
                              </button>
                              <button
                                className="btn btn-secondary"
                                onClick={handleResetFilters}
                                title={t("Réinitialiser")}
                              >
                                <i className="fa fa-redo"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Table */}
              <div className="row">
                <div className="col-12">
                  <div className="box">
                    <div className="box-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover" id="stock-table">
                          <thead>
                            <tr>
                              <th
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("titre")}
                              >
                                {t("Produit")}
                                {sortConfig.key === "titre" && (
                                  <i
                                    className={`fa fa-sort-${
                                      sortConfig.direction === "ascending"
                                        ? "up"
                                        : "down"
                                    } ml-2`}
                                  ></i>
                                )}
                              </th>
                              <th
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("quantiteStock")}
                              >
                                {t("Stock central")}
                                {sortConfig.key === "quantiteStock" && (
                                  <i
                                    className={`fa fa-sort-${
                                      sortConfig.direction === "ascending"
                                        ? "up"
                                        : "down"
                                    } ml-2`}
                                  ></i>
                                )}
                              </th>
                              {caisses.map((caisse) => (
                                <th key={caisse.id}>{caisse.nom}</th>
                              ))}
                              <th>{t("Unité")}</th>

                              <th>{t("Statut")}</th>
                              <th>{t("Actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedMenus.length > 0 ? (
                              sortedMenus.map((menu) => (
                                <tr
                                  key={menu._id || menu.id}
                                  // className={
                                  //   menu.stockStatus === "outOfStock"
                                  //     ? "table-danger"
                                  //     : menu.stockStatus === "lowStock"
                                  //       ? "table-warning"
                                  //       : ""
                                  // }
                                >
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {menu.image && menu.image.length > 0 ? (
                                        <img
                                          src={getImageUrl(menu.image)}
                                          alt={menu.titre?.fr}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                              "images/product/no-image.png";
                                          }}
                                          className="rounded mr-3"
                                          style={{
                                            width: "50px",
                                            height: "50px",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          className="rounded mr-3 bg-light d-flex align-items-center justify-content-center"
                                          style={{
                                            width: "50px",
                                            height: "50px",
                                          }}
                                        >
                                          <i className="fa fa-utensils text-muted"></i>
                                        </div>
                                      )}
                                      <div>
                                        <strong>
                                          {menu.titre?.fr || menu.titre}
                                        </strong>
                                        <small className="text-muted d-block">
                                          {menu.reference}
                                        </small>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge badge-${getMainStockQuantity(menu) > 0 ? "info" : "secondary"}`}
                                    >
                                      {getMainStockQuantity(menu)}
                                    </span>
                                  </td>
                                  {caisses.map((caisse) => (
                                    <td key={caisse.id}>
                                      <span className="badge badge-secondary">
                                        {getCaisseStockForProduct(
                                          menu._id,
                                          caisse.id,
                                        )}
                                      </span>
                                    </td>
                                  ))}
                                  <td onClick={() => console.log(menu)}>
                                    <span className={`badge badge-secondary`}>
                                      {menu?.main_stock?.unite}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge badge-${getStatusColor(
                                        menu.stockStatus,
                                      )}`}
                                    >
                                      {getStatusText(menu.stockStatus)}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="btn-group">
                                      <button
                                        className="btn btn-sm btn-primary"
                                        title={t("Distribuer aux caisses")}
                                        onClick={() =>
                                          handleDistributeStock(menu)
                                        }
                                        disabled={
                                          getMainStockQuantity(menu) === 0
                                        }
                                      >
                                        <i className="fa fa-share-alt"></i>
                                      </button>
                                      {/* <button
                                        className="btn btn-sm btn-info"
                                        title={t("Voir stocks par caisse")}
                                        onClick={() =>
                                          handleViewCaisseStock(menu)
                                        }
                                      >
                                        <i className="fa fa-eye"></i>
                                      </button> */}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={6 + caisses.length}
                                  className="text-center py-5"
                                >
                                  <i className="fa fa-box fa-3x text-muted mb-3"></i>
                                  <h5>{t("Aucun produit trouvé")}</h5>
                                  <p className="text-muted">
                                    {filters.search || filters.status !== "all"
                                      ? t(
                                          "Aucun produit ne correspond aux filtres appliqués",
                                        )
                                      : t(
                                          "Aucun produit prêt à servir trouvé dans votre stock",
                                        )}
                                  </p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center p-3 border-top">
                          <span className="text-muted">
                            {t("Affichage de")}{" "}
                            {(pagination.page - 1) * pagination.limit + 1}
                            {" à "}
                            {Math.min(
                              pagination.page * pagination.limit,
                              pagination.total,
                            )}
                            {" sur "}
                            {pagination.total} {t("produits")}
                          </span>

                          <nav>
                            <ul className="pagination mb-0">
                              <li
                                className={`page-item ${
                                  pagination.page === 1 ? "disabled" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => goToPage(pagination.page - 1)}
                                  disabled={pagination.page === 1}
                                >
                                  <i className="fa fa-chevron-left"></i>
                                </button>
                              </li>

                              {[...Array(pagination.totalPages)].map((_, i) => {
                                const pageNum = i + 1;
                                if (
                                  pageNum === 1 ||
                                  pageNum === pagination.totalPages ||
                                  (pageNum >= pagination.page - 2 &&
                                    pageNum <= pagination.page + 2)
                                ) {
                                  return (
                                    <li
                                      key={pageNum}
                                      className={`page-item ${
                                        pagination.page === pageNum
                                          ? "active"
                                          : ""
                                      }`}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() => goToPage(pageNum)}
                                      >
                                        {pageNum}
                                      </button>
                                    </li>
                                  );
                                } else if (
                                  pageNum === pagination.page - 3 ||
                                  pageNum === pagination.page + 3
                                ) {
                                  return (
                                    <li
                                      key={pageNum}
                                      className="page-item disabled"
                                    >
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }
                                return null;
                              })}

                              <li
                                className={`page-item ${
                                  pagination.page === pagination.totalPages
                                    ? "disabled"
                                    : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => goToPage(pagination.page + 1)}
                                  disabled={
                                    pagination.page === pagination.totalPages
                                  }
                                >
                                  <i className="fa fa-chevron-right"></i>
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Distribute Stock Modal */}
      {showDistributeModal && selectedProduct && (
        <DistributeStockModal
          show={showDistributeModal}
          onClose={() => {
            setShowDistributeModal(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          caisses={caisses}
          mainStock={getMainStockQuantity(selectedProduct)}
          caisseStocks={caisseStocks}
          onSubmit={handleDistributeSubmit}
        />
      )}

      {/* View Caisse Stock Modal */}
      {showCaisseStockModal && selectedProductForCaisse && (
        <CaisseStockModal
          show={showCaisseStockModal}
          onClose={() => {
            setShowCaisseStockModal(false);
            setSelectedProductForCaisse(null);
          }}
          product={selectedProductForCaisse}
          caisses={caisses}
          caisseStocks={caisseStocks}
        />
      )}
    </section>
  );
};

export default Stocks;